// KnowledgeBase Quasar-style Images plugin
// Enhances images in Preview to look like Quasar's QImg (lightweight, no Vue mount).
// - Wraps images with a styled container
// - Adds a loading skeleton that fades out on load
// - Preserves existing captions (from <figure><figcaption>) and titles

import { registerMarkdownPostProcessor } from '../../services/PluginAPI.js'
import './styles/images.css'

function enhanceStandaloneImage(img) {
  if (!img || img.__kb_qimg_applied) return
  // Skip YouTube replacements or already-enhanced nodes
  if (img.closest('.kb-yt')) return
  // Skip entity embed avatars/images to avoid breaking inline layout
  if (img.closest('.kb-entity-embed') || img.closest('.entity-embed')) return
  // Skip tiny inline icon images (from :img: markers) to keep them raw and borderless
  if (img.classList.contains('kb-icon-image') || img.closest('.kb-inline-icon')) return
  img.__kb_qimg_applied = true

  const parent = img.parentElement
  if (!parent) return

  const container = document.createElement('div')
  container.className = 'kb-qimg'

  const frame = document.createElement('div')
  frame.className = 'kb-qimg-frame'

  const skel = document.createElement('div')
  skel.className = 'kb-qimg-skel'

  // Respect explicit width attribute by constraining container
  const w = parseInt(img.getAttribute('width') || '', 10)
  if (w && Number.isFinite(w)) container.style.maxWidth = w + 'px'

  // Wiring load/error to toggle skeleton
  const onLoad = () => container.classList.add('loaded')
  const onError = () => container.classList.add('error')
  try {
    img.addEventListener('load', onLoad, { once: true })
    img.addEventListener('error', onError, { once: true })
    // If already complete (from cache), set loaded
    if (img.complete && img.naturalWidth > 0) container.classList.add('loaded')
  } catch {}

  parent.replaceChild(container, img)
  frame.appendChild(img)
  container.appendChild(frame)
  container.appendChild(skel)
}

function enhanceFigure(figure) {
  if (!figure || figure.__kb_qimg_applied) return
  if (figure.classList.contains('kb-yt-figure')) return
  if (figure.closest('.kb-entity-embed') || figure.closest('.entity-embed')) return
  const img = figure.querySelector('img')
  if (!img) return
  // Skip figures that are actually inline icon containers
  if (img.classList.contains('kb-icon-image') || img.closest('.kb-inline-icon')) return
  figure.__kb_qimg_applied = true

  figure.classList.add('kb-qimg-figure')

  const frame = document.createElement('div')
  frame.className = 'kb-qimg-frame'
  const skel = document.createElement('div')
  skel.className = 'kb-qimg-skel'

  const caption = figure.querySelector('figcaption')
  const before = caption || null

  const onLoad = () => figure.classList.add('loaded')
  const onError = () => figure.classList.add('error')
  try {
    img.addEventListener('load', onLoad, { once: true })
    img.addEventListener('error', onError, { once: true })
    if (img.complete && img.naturalWidth > 0) figure.classList.add('loaded')
  } catch {}

  // Move image into frame before caption
  img.parentElement && img.parentElement.removeChild(img)
  frame.appendChild(img)
  if (before) {
    figure.insertBefore(frame, before)
  } else {
    figure.appendChild(frame)
  }
  figure.appendChild(skel)
}

export function registerQuasarImagesPlugin() {
  registerMarkdownPostProcessor(
    (root) => {
      // Enhance figures first to preserve captions
      const figures = Array.from(root.querySelectorAll('figure.kb-embed-figure'))
      for (const f of figures) {
        try {
          enhanceFigure(f)
        } catch {}
      }

      // Enhance standalone images not already handled
      const imgs = Array.from(root.querySelectorAll('img'))
      for (const img of imgs) {
        try {
          if (img.closest('figure.kb-embed-figure')) continue
          if (img.closest('.kb-yt')) continue
          if (img.closest('.kb-entity-embed') || img.closest('.entity-embed')) continue
          if (img.closest('.kb-qimg') || img.closest('.kb-qimg-figure')) continue
          enhanceStandaloneImage(img)
        } catch {}
      }
    },
    { sortOrder: 45 }
  )
}

export default { registerQuasarImagesPlugin }
